import { supabase } from '../config/supabase';

const CACHE_TTL = 30 * 60 * 1000; // 30 minuti

const deezerCache = new Map();

const buildCacheKey = (query, limit) => `tracks:${query?.toLowerCase() || ''}-${limit}`;
const buildPathCacheKey = (path, params) => `path:${path}:${JSON.stringify(params || {})}`;

/** Chiama Deezer tramite la nostra Edge Function (niente CORS, niente limiti proxy pubblici) */
const fetchViaEdgeFunction = async (query, limit) => {
    const { data, error } = await supabase.functions.invoke('deezer-proxy', {
        body: { query: String(query).trim(), limit: Math.min(Math.max(Number(limit) || 25, 1), 50) }
    });
    if (error) throw error;
    if (data && typeof data.data !== 'undefined') return data;
    return { data: [] };
};

/** Chiama un path generico Deezer tramite proxy, con cache */
const callDeezerPath = async (path, params = {}) => {
    const cacheKey = buildPathCacheKey(path, params);
    const cached = deezerCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
    }

    const { data, error } = await supabase.functions.invoke('deezer-proxy', {
        body: { path, params }
    });

    if (error) throw error;

    deezerCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
};

export const searchDeezerTracks = async (query, limit = 25) => {
    if (!query) {
        return { data: [] };
    }

    const cacheKey = buildCacheKey(query, limit);
    const cachedEntry = deezerCache.get(cacheKey);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL) {
        return cachedEntry.data;
    }

    let data = { data: [] };
    let requestSucceeded = false;
    try {
        data = await fetchViaEdgeFunction(query, limit);
        requestSucceeded = true;
    } catch (e) {
        console.warn('[Deezer] Edge Function non disponibile o errore, anteprime non disponibili.', e?.message || e);
    }

    // Cache solo risposte riuscite: non fissare risultati vuoti in cache dopo errori temporanei
    if (requestSucceeded) {
        deezerCache.set(cacheKey, {
            data,
            timestamp: Date.now()
        });
    }

    return data;
};

// ─── Utilità per name matching ────────────────────────────────────────────────

const normalizeStr = (s) =>
    (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // rimuove accenti
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

/** Placeholder Deezer ufficiale (silhouette grigia, MD5 stringa vuota) */
const isDeezerPlaceholderImage = (url) => {
    if (!url || typeof url !== 'string') return true;
    return /d41d8cd98f00b204e9800998ecf8427e/i.test(url)
        || /1000x1000-000000-80-0-0/i.test(url)
        || /\/images\/artist\/\/+/i.test(url);
};

const hasRealArtistPicture = (artist) => {
    const pics = [artist?.picture_xl, artist?.picture_big, artist?.picture_medium].filter(Boolean);
    return pics.some((p) => !isDeezerPlaceholderImage(p));
};
const isNameMatch = (requested, candidate) => {
    const a = normalizeStr(requested);
    const b = normalizeStr(candidate);
    return a === b || a.includes(b) || b.includes(a);
};

// ─── Ricerca artista su Deezer ─────────────────────────────────────────────────

/**
 * Cerca un artista su Deezer e restituisce i dati normalizzati nel formato Spotify
 * (stesso shape: id, name, images[], external_urls.spotify, genres, albums[])
 * per essere drop-in replacement in ArtistSlide e nel filtro.
 *
 * Per il link Spotify usa open.spotify.com/search (non serve Spotify API).
 */
export const searchDeezerArtist = async (artistName, genreHint = null, originHint = null, skipAlbums = false) => {
    if (!artistName) return null;

    try {
        const response = await callDeezerPath('/search/artist', { q: artistName, limit: 10 });

        if (!response?.data?.length) return null;

        // 1. Filtra artisti senza immagine reale (salta placeholder silhouette Deezer)
        const withImage = response.data.filter(hasRealArtistPicture);
        if (!withImage.length) return null;

        // 2. Cerca il miglior match per nome (priorità: esatto, poi parziale)
        let best = null;

        // Esatto
        best = withImage.find(a => normalizeStr(a.name) === normalizeStr(artistName));

        // Parziale
        if (!best) {
            best = withImage.find(a => isNameMatch(artistName, a.name));
        }

        // Fallback: primo con immagine (Deezer ordina già per rilevanza)
        if (!best) {
            best = withImage[0];
        }

        if (!best) return null;

        // 3. Normalizza nel formato Spotify (shape identico → ArtistSlide non cambia)
        const normalizedArtist = {
            id: best.id,
            name: best.name,
            images: [
                { url: best.picture_xl },
                { url: best.picture_big },
                { url: best.picture_medium }
            ].filter(img => img.url && !isDeezerPlaceholderImage(img.url)),
            external_urls: {
                // Link Spotify senza API: cerca per nome (sempre funziona)
                spotify: `https://open.spotify.com/search/${encodeURIComponent(best.name)}`
            },
            genres: [], // Deezer non espone generi nella ricerca artista
            nb_fan: best.nb_fan || 0,
            deezerLink: best.link,
            _provider: 'deezer'
        };

        if (!normalizedArtist.images.length) return null;

        // 4. Carica album solo se richiesto (come skipAlbums in Spotify)
        if (!skipAlbums) {
            normalizedArtist.albums = await _fetchDeezerAlbums(best.id);
        }

        return normalizedArtist;

    } catch (e) {
        console.error('[Deezer] searchDeezerArtist error for', artistName, ':', e?.message || e);
        return null;
    }
};

// ─── Album ─────────────────────────────────────────────────────────────────────

const _fetchDeezerAlbums = async (deezerArtistId) => {
    try {
        const response = await callDeezerPath(`/artist/${deezerArtistId}/albums`, { limit: 10 });
        return _normalizeDeezerAlbums(response?.data || []);
    } catch {
        return [];
    }
};

const _normalizeDeezerAlbums = (items) =>
    items
        .filter(a => !a.record_type || a.record_type === 'album')
        .map(a => ({
            id: a.id,
            name: a.title,
            images: [
                { url: a.cover_xl },
                { url: a.cover_big },
                { url: a.cover_medium }
            ].filter(img => img.url),
            release_date: a.release_date || null,
            album_type: 'album',
            _provider: 'deezer'
        }))
        .slice(0, 10);

/**
 * Carica gli album di un artista Deezer, restituisce { items: [...] }
 * (stesso shape di SpotifyService.getArtistAlbums → drop-in replacement)
 */
export const getDeezerArtistAlbums = async (deezerArtistId) => {
    const albums = await _fetchDeezerAlbums(deezerArtistId);
    return { items: albums };
};
